/**
 * ACRES User — My submissions list with status pills + revision reply
 */
import React, { useEffect, useState } from 'react';
import { acres } from '../../../lib/services/acresService';

interface Sub {
  id: string; template_id: string; template_title?: string; template_level?: number; template_points?: number;
  status: string; submitted_at: string; reviewed_at?: string;
  rejection_reason?: string; revision_notes?: string;
  proof_text?: string; proof_url?: string;
  awaiting_second_approval?: boolean;
}

const STATUS_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  pending:    { bg: 'rgba(96,165,250,0.15)', fg: '#60A5FA', label: '⏳ Pending Review' },
  in_review:  { bg: 'rgba(245,158,11,0.15)', fg: '#F59E0B', label: '👁️ In Review' },
  awaiting_second: { bg: 'rgba(245,158,11,0.15)', fg: '#F59E0B', label: '👁️ Awaiting 2nd Mod' },
  approved:   { bg: 'rgba(16,163,74,0.15)',  fg: '#16A34A', label: '✅ Approved' },
  rejected:   { bg: 'rgba(220,38,38,0.15)',  fg: '#F87171', label: '✗ Rejected' },
  revision_requested: { bg: 'rgba(245,158,11,0.2)', fg: '#FBBF24', label: '↻ Revision Requested' }
};

export const AcresMySubmissions: React.FC<{ user?: any }> = () => {
  const [items, setItems] = useState<Sub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [active, setActive] = useState<Sub | null>(null);
  const [revisionText, setRevisionText] = useState('');
  const [revisionFile, setRevisionFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try { const r = await acres.getMySubmissions({ limit: 100 }); setItems(r.submissions || []); }
    catch (e: any) { setError(e?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fileToBase64 = (f: File): Promise<string> => new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(String(r.result || '').split(',').pop() || ''); r.onerror = rej; r.readAsDataURL(f);
  });

  const resubmit = async () => {
    if (!active) return;
    if (!revisionText && !revisionFile) { alert('Add a description or attach proof'); return; }
    setBusy(true);
    try {
      const payload: any = { submission_id: active.id, proof_text: revisionText || undefined,
        idempotency_key: `re_${active.id}_${Date.now()}` };
      if (revisionFile) {
        if (revisionFile.size > 10 * 1024 * 1024) throw new Error('File too large (max 10 MB)');
        payload.proof_file = { name: revisionFile.name, mime: revisionFile.type, size: revisionFile.size, data_base64: await fileToBase64(revisionFile) };
      }
      await acres.resubmitTask(payload);
      setActive(null); setRevisionText(''); setRevisionFile(null);
      await load();
      alert('Resubmitted for review.');
    } catch (e: any) { alert(e?.message || 'Resubmission failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <div style={{ color: '#9CA3AF' }}>Loading…</div>;
  if (error) return <div style={errBox}>{error}</div>;

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead><tr style={trHead}>
            <th style={th}>Submitted</th><th style={th}>Task</th><th style={th}>Points</th>
            <th style={th}>Status</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {items.map(s => {
              const statusKey = s.awaiting_second_approval ? 'awaiting_second' : s.status;
              const sc = STATUS_COLORS[statusKey] || { bg: '#1E1E1E', fg: '#FFF', label: statusKey };
              return (
                <tr key={s.id} style={trBody}>
                  <td style={td}>{new Date(s.submitted_at).toLocaleString()}</td>
                  <td style={td}>L{s.template_level} · {s.template_title}</td>
                  <td style={td}>{s.template_points}</td>
                  <td style={td}><span style={{ background: sc.bg, color: sc.fg, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{sc.label}</span></td>
                  <td style={td}>
                    <button onClick={() => { setActive(s); setRevisionText(''); setRevisionFile(null); }} style={btnSmall}>View</button>
                  </td>
                </tr>
              );
            })}
            {!items.length && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>No submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{active.template_title}</div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>L{active.template_level} · {active.template_points} pts · {new Date(active.submitted_at).toLocaleString()}</div>

            {active.proof_text && <div style={info}><b>Your proof:</b><br />{active.proof_text}</div>}
            {active.proof_url && <a href={active.proof_url} target="_blank" rel="noopener noreferrer" style={{ color: '#F59E0B', fontSize: 13 }}>📎 View attachment</a>}

            {active.status === 'rejected' && active.rejection_reason && (
              <div style={errBoxInline}><b>Rejected:</b><br />{active.rejection_reason}</div>
            )}
            {active.status === 'revision_requested' && active.revision_notes && (
              <>
                <div style={warnBox}><b>Moderator requested revision:</b><br />{active.revision_notes}</div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 4 }}>Updated proof description</div>
                  <textarea value={revisionText} onChange={e => setRevisionText(e.target.value)} style={{ ...input, minHeight: 80 }} />
                  <div style={{ fontSize: 12, color: '#9CA3AF', margin: '8px 0 4px' }}>Replace attachment (optional)</div>
                  <input type="file" accept="image/*,video/*,application/pdf" onChange={e => setRevisionFile(e.target.files?.[0] || null)} style={input} />
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                    <button onClick={resubmit} disabled={busy} style={btnPrimary}>{busy ? 'Resubmitting…' : 'Resubmit'}</button>
                  </div>
                </div>
              </>
            )}
            {active.status === 'approved' && (
              <div style={okBox}>✅ Approved {active.reviewed_at ? `on ${new Date(active.reviewed_at).toLocaleString()}` : ''} · {active.template_points} points credited.</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setActive(null)} style={btnGhost}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const trHead: React.CSSProperties = { background: '#1E1E1E' };
const trBody: React.CSSProperties = { borderBottom: '1px solid #1E1E1E' };
const th: React.CSSProperties = { padding: '8px 10px', textAlign: 'left', color: '#F59E0B', fontWeight: 600 };
const td: React.CSSProperties = { padding: '8px 10px' };
const input: React.CSSProperties = { width: '100%', padding: '8px 10px', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 6, color: '#FFF', fontSize: 13 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modal: React.CSSProperties = { background: '#121212', border: '1px solid #2A2A2A', borderRadius: 12, padding: 20, width: 'min(560px, 100%)', maxHeight: '90vh', overflowY: 'auto' };
const info: React.CSSProperties = { padding: 8, background: '#0A0A0A', border: '1px solid #1E1E1E', borderRadius: 6, fontSize: 12, marginBottom: 6, whiteSpace: 'pre-wrap' };
const errBox: React.CSSProperties = { padding: 10, background: 'rgba(220,38,38,0.1)', border: '1px solid #DC2626', borderRadius: 6, color: '#FCA5A5', marginBottom: 10 };
const errBoxInline: React.CSSProperties = { ...errBox, marginTop: 8 };
const warnBox: React.CSSProperties = { marginTop: 8, padding: 10, background: 'rgba(245,158,11,0.1)', border: '1px solid #F59E0B', borderRadius: 6, color: '#FBBF24', fontSize: 13 };
const okBox: React.CSSProperties = { marginTop: 8, padding: 10, background: 'rgba(16,163,74,0.1)', border: '1px solid #16A34A', borderRadius: 6, color: '#4ADE80', fontSize: 13 };
const btnSmall: React.CSSProperties = { padding: '4px 10px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 4, fontSize: 12, cursor: 'pointer' };
const btnPrimary: React.CSSProperties = { padding: '8px 14px', background: '#F59E0B', color: '#000', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer' };
const btnGhost: React.CSSProperties = { padding: '8px 14px', background: 'transparent', color: '#E5E7EB', border: '1px solid #374151', borderRadius: 6, cursor: 'pointer' };
